import React from 'react'
import './styles.scss'
import {
  resident,
  staff,
  background,
  house,
  numbers,
  grave
} from '../_svgpaths'
import { Isotype } from 'components/Isotype'
import useDimensions from './useDimensions'
import { formatDate } from '../_utils'

export const ResidentChartSmall = ({
  data,
  locales,
  lang
}: {
  data: drawDataType
  locales: { [key: string]: string }
  lang: 'de' | 'fr'
}) => {
  const [SVGEL, Size, Node] = useDimensions()
  const totaldata = data.floors
    .reduce((a, b) => a.concat(b), [])
    .sort((a, b) => b.localeCompare(a))

  const circleRad = Math.min(Size.width / (30 * 2), 8) || 0

  const offset = circleRad * 2 || 0
  const alignX = (Size.width - 28 * offset) / 2 || 0

  const Circle = <circle r={circleRad} />
  return (
    <div className='ResidentChartSmall'>
      <svg
        ref={SVGEL}
        height={
          data.death[0].includes('death') || data.staff[0].includes('staff')
            ? 14 * offset
            : 9 * offset
        }
      >
        <Text
          text={formatDate(data.date, lang)}
          x={alignX}
          y={30}
          className='title'
          anchor='start'
        />
        <Text
          text={locales.residents}
          x={alignX}
          y={52}
          className='label'
          anchor='start'
        />
        <Isotype
          data={[totaldata]}
          x={alignX + 5}
          y={65}
          xOffset={offset}
          yOffset={offset}
          icon={Circle}
          wrapAfter={28}
        />
        {data.staff[0].includes('staff') && (
          <>
            <Text
              text={locales.staff}
              x={alignX}
              y={52 + offset * 5}
              anchor='start'
              className='label'
            />{' '}
          </>
        )}
        <Isotype
          data={data.staff}
          x={alignX + 5}
          y={65 + offset * 5}
          xOffset={offset}
          yOffset={offset}
          icon={Circle}
          wrapAfter={10}
        />

        {data.death[0].includes('death') && (
          <>
            <Text
              text={locales.death + formatDate(data.deathDate)}
              x={alignX + 16 * offset}
              y={52 + offset * 5}
              anchor='start'
              className='label'
            />{' '}
          </>
        )}
        <Isotype
          data={data.death}
          x={alignX + 16 * offset + 5}
          y={65 + offset * 5}
          xOffset={offset}
          yOffset={offset}
          icon={Circle}
          wrapAfter={12}
        />
      </svg>
    </div>
  )
}
export const ResidentChart = ({
  data,
  locales,
  lang = 'de'
}: {
  data: drawDataType
  locales: { [key: string]: string }
  lang?: 'de' | 'fr'
}) => {
  const [SVGEL, Size, Node] = useDimensions()
  const alignX = Size.width - 30||0

  return (
    <div className='ResidentChart'>
      <svg ref={SVGEL}>
        <Background x={Size.width - 800 * 0.6 - 60} y={50} s={0.6} />
        <Text
          text={formatDate(data.date, lang)}
          x={alignX}
          y={50}
          className='title'
          anchor={'end'}
        />
        <Text
          text={locales.residents}
          x={alignX}
          y={86}
          className='label'
          anchor={'end'}
        />
        <Isotype
          data={[...data.floors].reverse()}
          x={alignX - 6}
          y={116}
          xOffset={13}
          yOffset={41.5}
          icon={<path d={resident} />}
          direction={'rl'}
          wrapAfter={16}
        />
        {data.staff[0].includes('staff') && (
          <>
            <Text
              text={locales.staff}
              x={alignX}
              y={332}
              anchor={'end'}
              className='label'
            />
            <Isotype
              data={data.staff}
              x={alignX - 6}
              y={362}
              xOffset={13}
              yOffset={41.5}
              icon={<path d={staff} />}
              direction={'rl'}
              wrapAfter={16}
            />
          </>
        )}
        {data.death[0].includes('death') && (
          <>
            <Text
              text={locales.death + formatDate(data.deathDate)}
              x={alignX}
              y={470}
              anchor={'end'}
              className='label'
            />
            <Isotype
              data={data.death}
              x={alignX - 6}
              y={490}
              xOffset={13}
              yOffset={41.5}
              icon={<path d={grave} />}
              direction={'rl'}
              wrapAfter={16}
            />
          </>
        )}
      </svg>
    </div>
  )
}

const Text = ({
  x,
  y,
  text,
  anchor = 'start',
  className = ''
}: {
  x?: number
  y?: number
  text?: string
  className?: string
  anchor?: 'end' | 'start' | 'middle'
}) => {
  return (
    <text textAnchor={anchor} x={x} y={y} className={className}>
      {text}
    </text>
  )
}
const Background = ({ x, y, s }: { x: number; y: number; s: number }) => {
  return (
    <g className='bg' transform={`translate(${x},${y})scale(${s})`}>
      <path d={background} id='background' />
      <path d={house} id='house' />
      <path d={numbers} id='numbers' />
    </g>
  )
}
